// WebSIG do contrato — mapa interativo MapLibre com camadas (KMZ/KML/SHP/GeoJSON).
// Espelho do MapaNegocio mas vinculado a operacao.contratos.id (não comercial.propostas.id).
// O usuário sobe arquivos, calcula área/perímetro/bbox no client e salva no backend.
// Endpoints op_*_geometria(s)_contrato em comercial-engine/handlers/geometriasContrato.js.
import { useEffect, useRef, useState } from 'react';
import maplibregl, { Map as MapLibreMap, LngLatBounds } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../config';
import IconLixeira from './IconLixeira';
import { parseGeoArquivo, fmtArea, fmtDistancia } from '../lib/geoParse';
import type { FeatureCollection } from 'geojson';

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface Geometria {
  id: string;
  contrato_id: string;
  nome: string;
  tipo: string;
  tipo_levantamento: string | null;  // planialtimetrico | cadastral | bacia_hidrografica | null
  geojson: FeatureCollection;
  area_m2: number | null;
  perimetro_m: number | null;
  comprimento_m: number | null;
  bbox: [number, number, number, number] | null;
  n_features: number | null;
  cor: string;
  visivel: boolean;
  observacoes: string | null;
  arquivo_origem_nome: string | null;
  criado_em: string;
}

const CORES_PADRAO = ['#0930D0', '#0a7f4a', '#B07500', '#7C3AED', '#B91626', '#0284C7', '#DB2777', '#F59E0B'];

const ICONES_FEATURE: Record<string, string> = {
  Polygon: '⬡', MultiPolygon: '⬡',
  LineString: '╱', MultiLineString: '╱',
  Point: '📍', MultiPoint: '📍',
};

function btnDesenho(ativo: boolean): React.CSSProperties {
  return {
    padding: '6px 8px', fontSize: 14, border: 'none', cursor: 'pointer',
    background: ativo ? '#FF6B00' : '#fff',
    color: ativo ? '#fff' : '#444',
    borderTop: '0.5px solid #ddd',
    minWidth: 32, lineHeight: 1,
  };
}

// Linha de diagnóstico do upload — visível na UI sem abrir DevTools
type LinhaDiag = { hora: string; nivel: 'info' | 'ok' | 'erro'; texto: string };

// Spinner inline pra indicar processamento dentro do botão "+ Adicionar"
function SpinnerInline() {
  return (
    <>
      <style>{`
        @keyframes mapaSpin { to { transform: rotate(360deg); } }
        .mapa-spin {
          width: 11px; height: 11px; border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          animation: mapaSpin 0.8s linear infinite;
          display: inline-block;
        }
      `}</style>
      <span className="mapa-spin" />
    </>
  );
}

export default function MapaContrato({ contratoId }: { contratoId: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [geometrias, setGeometrias] = useState<Geometria[]>([]);
  const [loading, setLoading] = useState(true);
  const [importando, setImportando] = useState<string | null>(null);
  const [estiloBase, setEstiloBase] = useState<'osm' | 'satelite'>('osm');
  const [diagnostico, setDiagnostico] = useState<LinhaDiag[]>([]);
  const [diagAberto, setDiagAberto] = useState(false);
  const [modoDesenho, setModoDesenho] = useState<'nenhum' | 'polygon' | 'line' | 'point'>('nenhum');
  const [pontosDesenho, setPontosDesenho] = useState<[number, number][]>([]);
  const [popupFeature, setPopupFeature] = useState<any | null>(null);
  // Fotos do diário (PWA campo) — camada virtual com clustering MapLibre.
  // Dados sempre frescos via fetch (sem persistir em operacao.geometrias_contrato).
  const [fotosFC, setFotosFC] = useState<FeatureCollection | null>(null);
  const [fotosVisivel, setFotosVisivel] = useState(true);
  const [popupFoto, setPopupFoto] = useState<any | null>(null);
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const camadasIdsRef = useRef<Set<string>>(new Set());
  const modoDesenhoRef = useRef(modoDesenho);
  const pontosDesenhoRef = useRef(pontosDesenho);
  // Mantém refs sincronizadas para uso dentro de handlers do mapa
  modoDesenhoRef.current = modoDesenho;
  pontosDesenhoRef.current = pontosDesenho;

  // Adiciona linha ao diagnóstico (visível na UI). Se for erro, abre o painel.
  function diag(nivel: LinhaDiag['nivel'], texto: string) {
    const hora = new Date().toLocaleTimeString('pt-BR');
    setDiagnostico(prev => [...prev, { hora, nivel, texto }]);
    if (nivel === 'erro') setDiagAberto(true);
    if (nivel === 'erro') console.error('[MapaContrato]', texto);
    else console.log('[MapaContrato]', texto);
  }
  function copiarDiagnostico() {
    const txt = diagnostico.map(l => `[${l.hora}] ${l.nivel.toUpperCase()}: ${l.texto}`).join('\n');
    navigator.clipboard.writeText(txt).then(() => {
      alert('Diagnóstico copiado! Cola num e-mail/Telegram pro Lucas/Claude.');
    });
  }

  // ── Inicializa o mapa quando o componente monta ────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: estiloBase === 'satelite' ? estiloSatelite() : estiloOsm(),
      center: [-51.93, -30.03],   // Porto Alegre como fallback (Hydrodata é RS)
      zoom: 6,
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
    mapRef.current = map;

    // ── Listeners de desenho ─────────────────────────────────────────────────
    function aoClicar(e: maplibregl.MapMouseEvent) {
      const modo = modoDesenhoRef.current;
      if (modo === 'nenhum') return;
      const pt: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      if (modo === 'point') {
        // Ponto único — finaliza imediatamente
        setPontosDesenho([pt]);
        // Aguarda o React aplicar o setState antes de finalizar
        setTimeout(() => finalizarDesenho([pt], 'point'), 50);
        return;
      }
      setPontosDesenho(prev => [...prev, pt]);
    }
    function aoDuploClicar(e: maplibregl.MapMouseEvent) {
      const modo = modoDesenhoRef.current;
      if (modo === 'nenhum') return;
      e.preventDefault();
      const pts = pontosDesenhoRef.current;
      if (modo === 'polygon' && pts.length >= 3) finalizarDesenho(pts, 'polygon');
      else if (modo === 'line' && pts.length >= 2) finalizarDesenho(pts, 'line');
    }
    map.on('click', aoClicar);
    map.on('dblclick', aoDuploClicar);

    // Cancelar desenho com Esc
    function aoTeclarEsc(e: KeyboardEvent) {
      if (e.key === 'Escape' && modoDesenhoRef.current !== 'nenhum') {
        cancelarDesenho();
      }
    }
    document.addEventListener('keydown', aoTeclarEsc);

    return () => {
      document.removeEventListener('keydown', aoTeclarEsc);
      map.remove();
      mapRef.current = null;
      camadasIdsRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fotos do diário (PWA campo): carrega GeoJSON + adiciona camada com cluster
  useEffect(() => {
    let cancelado = false;
    async function carregarFotos() {
      try {
        const token = await getToken();
        const r = await axios.get(
          `${API_URL}/mobile/contratos/${contratoId}/fotos-geojson`,
          { headers: { Authorization: token } });
        const fc: FeatureCollection = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (cancelado) return;
        setFotosFC(fc);
        adicionarCamadaFotos(fc);
      } catch (e: any) {
        console.warn('[MapaContrato] fotos do diário não carregadas:', e?.message);
      }
    }
    // Aguarda o style do mapa carregar antes de adicionar camada
    const map = mapRef.current;
    if (map && map.isStyleLoaded()) carregarFotos();
    else if (map) map.once('load', carregarFotos);
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoId]);

  function adicionarCamadaFotos(fc: FeatureCollection) {
    const map = mapRef.current;
    if (!map) return;
    const SRC = '__fotos_campo__';
    // Se source já existe, só atualiza os dados
    if (map.getSource(SRC)) {
      (map.getSource(SRC) as any).setData(fc);
      return;
    }
    map.addSource(SRC, {
      type: 'geojson',
      data: fc as any,
      cluster: true,
      clusterRadius: 50,
      clusterMaxZoom: 14,
    });
    // Clusters: círculo laranja com tamanho crescente conforme contagem
    map.addLayer({
      id: 'fotos-clusters', source: SRC, type: 'circle',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#FF6B00',
        'circle-radius': ['step', ['get', 'point_count'], 14, 10, 18, 50, 24],
        'circle-stroke-color': '#fff', 'circle-stroke-width': 2,
        'circle-opacity': 0.85,
      },
    });
    // Texto com a contagem dentro do cluster
    map.addLayer({
      id: 'fotos-clusters-count', source: SRC, type: 'symbol',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-size': 12,
      },
      paint: { 'text-color': '#fff' },
    });
    // Pontos individuais (quando zoom é alto o bastante)
    map.addLayer({
      id: 'fotos-pontos', source: SRC, type: 'circle',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#FF6B00', 'circle-radius': 6,
        'circle-stroke-color': '#fff', 'circle-stroke-width': 1.5,
      },
    });
    // Click em cluster → zoom in
    map.on('click', 'fotos-clusters', (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['fotos-clusters'] });
      const clusterId = features[0].properties?.cluster_id;
      const src = map.getSource(SRC) as any;
      src.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
        if (err) return;
        map.easeTo({ center: (features[0].geometry as any).coordinates, zoom, duration: 400 });
      });
    });
    // Click em ponto → popup com metadados
    map.on('click', 'fotos-pontos', (e) => {
      const f = e.features?.[0];
      if (!f) return;
      setPopupFoto({ ...f, _props: f.properties });
    });
    // Cursor pointer
    ['fotos-clusters', 'fotos-pontos'].forEach(id => {
      map.on('mouseenter', id, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', id, () => { map.getCanvas().style.cursor = ''; });
    });
  }

  function toggleFotosVisivel() {
    const map = mapRef.current;
    if (!map) return;
    const novo = !fotosVisivel;
    setFotosVisivel(novo);
    const vis = novo ? 'visible' : 'none';
    ['fotos-clusters', 'fotos-clusters-count', 'fotos-pontos'].forEach(id => {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis);
    });
  }

  // Abre foto numa nova aba (presigned URL). iOS Safari precisa abrir
  // window.open síncrono dentro do click — não pode ser depois de await.
  function abrirFoto(url: string) {
    const w = window.open(url, '_blank');
    if (!w) alert('Permita pop-ups deste site pra abrir fotos.');
  }

  // ── Atualiza preview do desenho no mapa quando pontos mudam ────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const SRC = '__draw_preview__';
    const LAYER_FILL = '__draw_fill__';
    const LAYER_LINE = '__draw_line__';
    const LAYER_PT = '__draw_pt__';

    function aplicar() {
      // Remove versão anterior
      [LAYER_FILL, LAYER_LINE, LAYER_PT].forEach(id => {
        try { if (map!.getLayer(id)) map!.removeLayer(id); } catch {}
      });
      try { if (map!.getSource(SRC)) map!.removeSource(SRC); } catch {}
      if (modoDesenho === 'nenhum' || pontosDesenho.length === 0) return;

      const features: any[] = [];
      // Pontos sempre desenhados
      for (const [lng, lat] of pontosDesenho) {
        features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: {} });
      }
      // Linha conectando se ≥2
      if ((modoDesenho === 'line' || modoDesenho === 'polygon') && pontosDesenho.length >= 2) {
        features.push({ type: 'Feature',
          geometry: { type: 'LineString', coordinates: pontosDesenho }, properties: {} });
      }
      // Polígono fechado se ≥3
      if (modoDesenho === 'polygon' && pontosDesenho.length >= 3) {
        const ring = [...pontosDesenho, pontosDesenho[0]];
        features.push({ type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [ring] }, properties: {} });
      }
      try {
        map!.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features } as any });
        map!.addLayer({
          id: LAYER_FILL, source: SRC, type: 'fill',
          paint: { 'fill-color': '#FF6B00', 'fill-opacity': 0.2 },
          filter: ['==', ['geometry-type'], 'Polygon'],
        });
        map!.addLayer({
          id: LAYER_LINE, source: SRC, type: 'line',
          paint: { 'line-color': '#FF6B00', 'line-width': 3, 'line-dasharray': [2, 2] },
        });
        map!.addLayer({
          id: LAYER_PT, source: SRC, type: 'circle',
          paint: { 'circle-radius': 6, 'circle-color': '#FF6B00',
                   'circle-stroke-color': '#fff', 'circle-stroke-width': 2 },
          filter: ['==', ['geometry-type'], 'Point'],
        });
      } catch (e) { console.error('preview desenho:', e); }
    }
    if (map.isStyleLoaded()) aplicar();
    else map.once('styledata', aplicar);
  }, [modoDesenho, pontosDesenho]);

  function cancelarDesenho() {
    setModoDesenho('nenhum');
    setPontosDesenho([]);
  }

  async function finalizarDesenho(pts: [number, number][], tipo: 'polygon' | 'line' | 'point') {
    if (!pts.length) return cancelarDesenho();
    const nome = window.prompt(
      `Dê um nome para a ${tipo === 'polygon' ? 'área desenhada' : tipo === 'line' ? 'linha desenhada' : 'marcação'}:`,
      tipo === 'polygon' ? 'Polígono manual' : tipo === 'line' ? 'Linha manual' : 'Ponto manual'
    );
    if (!nome) return cancelarDesenho();

    // Monta GeoJSON
    let feature: any;
    if (tipo === 'polygon') {
      const ring = [...pts, pts[0]];
      feature = { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } };
    } else if (tipo === 'line') {
      feature = { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: pts } };
    } else {
      feature = { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: pts[0] } };
    }
    const geojson = { type: 'FeatureCollection', features: [feature] };

    // Calcula métricas pelo turf
    const turfMod = await import('@turf/turf');
    let area_m2 = 0, perimetro_m = 0, comprimento_m = 0;
    try {
      if (tipo === 'polygon') {
        area_m2 = turfMod.area(feature as any);
        const linha = turfMod.polygonToLine(feature as any);
        if (linha) {
          if ((linha as any).type === 'FeatureCollection') {
            for (const sub of (linha as any).features) perimetro_m += turfMod.length(sub, { units: 'meters' });
          } else {
            perimetro_m += turfMod.length(linha as any, { units: 'meters' });
          }
        }
      } else if (tipo === 'line') {
        comprimento_m = turfMod.length(feature as any, { units: 'meters' });
      }
    } catch {/* ignora */}
    const b = turfMod.bbox(geojson as any);
    const bbox: [number, number, number, number] = [b[0], b[1], b[2], b[3]];

    cancelarDesenho();

    // Salva como geometria 'manual'
    setImportando(`(desenho) ${nome}`);
    setDiagnostico([]);
    setDiagAberto(true);
    diag('info', `Salvando desenho "${nome}" como camada manual...`);
    try {
      const token = await getToken();
      const corNova = CORES_PADRAO[geometrias.length % CORES_PADRAO.length];
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_criar_geometria_contrato',
          contrato_id: contratoId,
          nome, tipo: 'manual',
          geojson, area_m2, perimetro_m, comprimento_m, bbox, n_features: 1, cor: corNova },
        { headers: { Authorization: token }, timeout: 30000 });
      const nova = r.data.body ? JSON.parse(r.data.body) : r.data;
      const novaCompleta: Geometria = { ...nova, geojson };
      setGeometrias(prev => [...prev, novaCompleta]);
      adicionarCamadaNoMapa(novaCompleta);
      if (novaCompleta.bbox) centralizarEm(novaCompleta);
      diag('ok', `✓ Desenho salvo como camada (id=${nova.id})`);
      setTimeout(() => setDiagAberto(false), 3000);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || String(e);
      diag('erro', `Falha ao salvar desenho: ${msg}`);
    } finally {
      setImportando(null);
    }
  }

  // ── Troca de estilo base (OSM ↔ Satélite) ──────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(estiloBase === 'satelite' ? estiloSatelite() : estiloOsm());
    // Re-adiciona camadas após troca de estilo
    map.once('styledata', () => {
      camadasIdsRef.current.clear();
      for (const g of geometrias) adicionarCamadaNoMapa(g);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estiloBase]);

  // ── Carrega geometrias do negócio ─────────────────────────────────────────
  useEffect(() => { carregar(); }, [contratoId]);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_listar_geometrias_contrato', contrato_id: contratoId, com_geojson: true },
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      const lista: Geometria[] = Array.isArray(data) ? data : [];
      setGeometrias(lista);
      // Popular o mapa
      const map = mapRef.current;
      if (map && map.isStyleLoaded()) renderizarTodas(lista);
      else if (map) map.once('styledata', () => renderizarTodas(lista));
    } catch (e: any) {
      console.error('Erro carregando geometrias:', e);
    } finally { setLoading(false); }
  }

  function renderizarTodas(lista: Geometria[]) {
    const map = mapRef.current;
    if (!map) return;
    // Remove camadas antigas
    camadasIdsRef.current.forEach(id => {
      try {
        if (map.getLayer(`${id}-fill`)) map.removeLayer(`${id}-fill`);
        if (map.getLayer(`${id}-line`)) map.removeLayer(`${id}-line`);
        if (map.getLayer(`${id}-point`)) map.removeLayer(`${id}-point`);
        if (map.getSource(id)) map.removeSource(id);
      } catch {/* ignora */}
    });
    camadasIdsRef.current.clear();
    // Adiciona todas + faz fitBounds em cima de tudo
    for (const g of lista) adicionarCamadaNoMapa(g);
    // FitBounds geral
    const todasBboxes = lista.filter(g => g.visivel !== false && g.bbox).map(g => g.bbox!);
    if (todasBboxes.length > 0) {
      const merged = mergeBoundsArray(todasBboxes);
      map.fitBounds([[merged[0], merged[1]], [merged[2], merged[3]]],
        { padding: 50, duration: 600, maxZoom: 16 });
    }
  }

  function adicionarCamadaNoMapa(g: Geometria) {
    const map = mapRef.current;
    if (!map || !g.visivel) return;
    if (camadasIdsRef.current.has(g.id)) return;  // já adicionada
    try {
      map.addSource(g.id, { type: 'geojson', data: g.geojson as any });
      map.addLayer({
        id: `${g.id}-fill`, source: g.id, type: 'fill',
        paint: { 'fill-color': g.cor || '#0930D0', 'fill-opacity': 0.25 },
        filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
      });
      map.addLayer({
        id: `${g.id}-line`, source: g.id, type: 'line',
        paint: { 'line-color': g.cor || '#0930D0', 'line-width': 2.5 },
      });
      map.addLayer({
        id: `${g.id}-point`, source: g.id, type: 'circle',
        paint: {
          'circle-radius': 5,
          'circle-color': g.cor || '#0930D0',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1.5,
        },
        filter: ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']],
      });
      // Click em qualquer feature da camada → abre popup com metadados
      const aoClicarFeature = async (e: maplibregl.MapLayerMouseEvent) => {
        if (modoDesenhoRef.current !== 'nenhum') return;  // não interfere com desenho
        const f = e.features?.[0];
        if (!f) return;
        const turfMod = await import('@turf/turf');
        let areaFeature: number | undefined;
        let compFeature: number | undefined;
        let coords: string | undefined;
        try {
          if (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon') {
            areaFeature = turfMod.area(f as any);
          } else if (f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString') {
            compFeature = turfMod.length(f as any, { units: 'meters' });
          } else if (f.geometry.type === 'Point') {
            const c = (f.geometry as any).coordinates;
            coords = `${c[1].toFixed(6)}, ${c[0].toFixed(6)}`;
          }
        } catch {/* ignora */}
        setPopupFeature({
          ...f,
          _camadaNome: g.nome,
          _camadaCor: g.cor,
          _areaFeature: areaFeature,
          _compFeature: compFeature,
          _coords: coords,
        });
      };
      const setCursor = () => { map.getCanvas().style.cursor = 'pointer'; };
      const unsetCursor = () => { map.getCanvas().style.cursor = ''; };
      [`${g.id}-fill`, `${g.id}-line`, `${g.id}-point`].forEach(id => {
        map.on('click', id, aoClicarFeature);
        map.on('mouseenter', id, setCursor);
        map.on('mouseleave', id, unsetCursor);
      });
      camadasIdsRef.current.add(g.id);
    } catch (e) {
      console.error(`Erro ao adicionar camada ${g.nome}:`, e);
    }
  }

  function removerCamadaDoMapa(id: string) {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (map.getLayer(`${id}-fill`)) map.removeLayer(`${id}-fill`);
      if (map.getLayer(`${id}-line`)) map.removeLayer(`${id}-line`);
      if (map.getLayer(`${id}-point`)) map.removeLayer(`${id}-point`);
      if (map.getSource(id)) map.removeSource(id);
      camadasIdsRef.current.delete(id);
    } catch {/* ignora */}
  }

  function centralizarEm(g: Geometria) {
    const map = mapRef.current;
    if (!map || !g.bbox) return;
    map.fitBounds([[g.bbox[0], g.bbox[1]], [g.bbox[2], g.bbox[3]]],
      { padding: 60, duration: 600, maxZoom: 17 });
  }

  // ── Upload de novo arquivo ─────────────────────────────────────────────────
  async function uploadArquivo(file: File) {
    setImportando(file.name);
    setDiagnostico([]);  // limpa diagnóstico do upload anterior
    setDiagAberto(true);  // abre painel de progresso
    let etapa = 'parse';
    try {
      diag('info', `Iniciando upload "${file.name}" (${(file.size / 1024).toFixed(1)} KB, mime=${file.type || 'desconhecido'})`);
      // 1) Parse local (gera GeoJSON + métricas)
      etapa = 'parse';
      diag('info', '1/3 — Lendo e convertendo arquivo localmente...');
      const { geojson, metricas, tipo } = await parseGeoArquivo(file);
      diag('ok', `Parse OK — formato=${tipo}, ${metricas.n_features} features, área=${metricas.area_m2.toFixed(0)}m², bbox detectada`);
      const corNova = CORES_PADRAO[geometrias.length % CORES_PADRAO.length];

      // 2) Upload do arquivo original pra S3 (rastreabilidade) — não bloqueia se falhar
      etapa = 'upload-original';
      diag('info', '2/3 — Enviando arquivo original pra S3 (backup)...');
      const token = await getToken();
      let s3_key: string | null = null;
      if (file.size <= 30 * 1024 * 1024) {
        try {
          const ru = await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_geom_contrato_upload_url', contrato_id: contratoId, filename: file.name,
              content_type: file.type || 'application/octet-stream' },
            { headers: { Authorization: token } });
          const du = ru.data.body ? JSON.parse(ru.data.body) : ru.data;
          await axios.put(du.upload_url, file, {
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            timeout: 180000,
          });
          s3_key = du.s3_key as string;
          diag('ok', `Arquivo original salvo em S3 (${s3_key!.slice(-40)})`);
        } catch (e: any) {
          const msg = e?.response?.data?.error || e?.message || String(e);
          diag('info', `Aviso: backup S3 falhou (segue só com geojson): ${msg}`);
        }
      } else {
        diag('info', 'Arquivo >30MB — pulando backup S3.');
      }

      // 3) Salva geometria no banco
      etapa = 'salvar-banco';
      diag('info', '3/3 — Salvando geometria no banco...');
      const nomeBase = file.name.replace(/\.(kml|kmz|zip|geojson|json)$/i, '');
      const tamanhoEstimado = JSON.stringify(geojson).length;
      if (tamanhoEstimado > 8 * 1024 * 1024) {
        throw new Error(
          `GeoJSON gerado tem ${(tamanhoEstimado / 1024 / 1024).toFixed(1)} MB — ` +
          `acima do limite (8 MB) suportado por enquanto. ` +
          `Tente subir o arquivo em partes ou remover camadas/propriedades pesadas.`
        );
      }
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_criar_geometria_contrato',
          contrato_id: contratoId,
          nome: nomeBase,
          tipo,
          arquivo_origem_s3_key: s3_key,
          arquivo_origem_nome: file.name,
          geojson, ...metricas, cor: corNova },
        { headers: { Authorization: token }, timeout: 60000 });
      const nova = r.data.body ? JSON.parse(r.data.body) : r.data;
      diag('ok', `Camada criada com id=${nova.id}. Renderizando no mapa...`);
      // 4) Atualiza UI
      const novaCompleta: Geometria = { ...nova, geojson };
      setGeometrias(prev => [...prev, novaCompleta]);
      adicionarCamadaNoMapa(novaCompleta);
      if (novaCompleta.bbox) centralizarEm(novaCompleta);
      diag('ok', '✓ Concluído com sucesso!');
      // Auto-fecha painel após 3s em caso de sucesso
      setTimeout(() => setDiagAberto(false), 3000);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || String(e);
      const status = e?.response?.status ? ` (HTTP ${e.response.status})` : '';
      diag('erro', `FALHA na etapa "${etapa}"${status}: ${msg}`);
      if (e?.stack) diag('erro', `Stack: ${String(e.stack).slice(0, 400)}`);
    } finally {
      setImportando(null);
      if (inputFileRef.current) inputFileRef.current.value = '';
    }
  }

  // ── Edita propriedades da geometria (renomear, cor, visibilidade) ──────────
  async function atualizarGeom(id: string, updates: Partial<Geometria>) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atualizar_geometria_contrato', id, ...updates },
        { headers: { Authorization: token } });
      const atualizada = r.data.body ? JSON.parse(r.data.body) : r.data;
      setGeometrias(prev => prev.map(g => g.id === id ? { ...g, ...atualizada, geojson: g.geojson } : g));
      // Atualizar visualização do mapa
      if (updates.visivel !== undefined) {
        const map = mapRef.current;
        if (!map) return;
        if (updates.visivel) {
          const g = geometrias.find(x => x.id === id);
          if (g) adicionarCamadaNoMapa({ ...g, visivel: true });
        } else {
          removerCamadaDoMapa(id);
        }
      }
      if (updates.cor) {
        const map = mapRef.current;
        if (map) {
          try {
            if (map.getLayer(`${id}-fill`)) map.setPaintProperty(`${id}-fill`, 'fill-color', updates.cor);
            if (map.getLayer(`${id}-line`)) map.setPaintProperty(`${id}-line`, 'line-color', updates.cor);
            if (map.getLayer(`${id}-point`)) map.setPaintProperty(`${id}-point`, 'circle-color', updates.cor);
          } catch {/* ignora */}
        }
      }
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function excluirGeom(g: Geometria) {
    if (!window.confirm(`Excluir a camada "${g.nome}"? Esta ação não pode ser desfeita.`)) return;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_excluir_geometria_contrato', id: g.id },
        { headers: { Authorization: token } });
      removerCamadaDoMapa(g.id);
      setGeometrias(prev => prev.filter(x => x.id !== g.id));
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  function exportarGeoJsonGlobal() {
    const todasFeatures = geometrias.flatMap(g => (g.geojson?.features || []).map(f => ({
      ...f, properties: { ...(f.properties || {}), _camada: g.nome },
    })));
    const fc = { type: 'FeatureCollection', features: todasFeatures };
    const blob = new Blob([JSON.stringify(fc, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contrato-${contratoId}-geometrias.geojson`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // ── Métricas agregadas ─────────────────────────────────────────────────────
  const totalArea = geometrias.reduce((s, g) => s + (g.area_m2 || 0), 0);
  const totalPerim = geometrias.reduce((s, g) => s + (g.perimetro_m || 0), 0);
  const totalCompr = geometrias.reduce((s, g) => s + (g.comprimento_m || 0), 0);

  return (
    <div style={{ display: 'flex', gap: 12, height: 'calc(100vh - 280px)', minHeight: 480 }}
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
      onDrop={e => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) {
          diag('info', `Arquivo arrastado: ${f.name}`);
          uploadArquivo(f);
        }
      }}>
      {/* Mapa */}
      <div style={{ flex: 1, position: 'relative', borderRadius: 6, overflow: 'hidden',
        border: '0.5px solid #e0e0e0' }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        {importando && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: 12, pointerEvents: 'none',
          }}>
            <div style={{
              padding: '14px 22px', background: '#fff', borderRadius: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              display: 'flex', alignItems: 'center', gap: 12,
              fontSize: 13, fontWeight: 600, color: '#020D7F',
            }}>
              <span style={{ display: 'inline-block', width: 18, height: 18, borderRadius: '50%',
                border: '3px solid #E7ECFD', borderTopColor: '#0930D0',
                animation: 'mapaSpin 0.8s linear infinite' }} />
              Processando "{importando}"…
            </div>
          </div>
        )}
        {/* Botão de troca de estilo base */}
        <div style={{
          position: 'absolute', top: 10, left: 10, zIndex: 5,
          background: '#fff', borderRadius: 5, border: '0.5px solid #ddd',
          overflow: 'hidden', display: 'flex',
        }}>
          <button onClick={() => setEstiloBase('osm')}
            style={{
              padding: '6px 10px', fontSize: 11, border: 'none', cursor: 'pointer',
              background: estiloBase === 'osm' ? '#0930D0' : '#fff',
              color: estiloBase === 'osm' ? '#fff' : '#444', fontWeight: 600,
            }}>🗺️ Mapa</button>
          <button onClick={() => setEstiloBase('satelite')}
            style={{
              padding: '6px 10px', fontSize: 11, border: 'none', cursor: 'pointer',
              background: estiloBase === 'satelite' ? '#0930D0' : '#fff',
              color: estiloBase === 'satelite' ? '#fff' : '#444', fontWeight: 600,
            }}>🛰️ Satélite</button>
        </div>

        {/* Barra de ferramentas de desenho */}
        <div style={{
          position: 'absolute', top: 60, left: 10, zIndex: 5,
          background: '#fff', borderRadius: 5, border: '0.5px solid #ddd',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}>
          <button onClick={() => { setPontosDesenho([]); setModoDesenho(modoDesenho === 'polygon' ? 'nenhum' : 'polygon'); }}
            title="Desenhar polígono — clique pra adicionar vértices, duplo-clique pra finalizar"
            style={btnDesenho(modoDesenho === 'polygon')}>⬡</button>
          <button onClick={() => { setPontosDesenho([]); setModoDesenho(modoDesenho === 'line' ? 'nenhum' : 'line'); }}
            title="Desenhar linha — clique pra adicionar pontos, duplo-clique pra finalizar"
            style={btnDesenho(modoDesenho === 'line')}>╱</button>
          <button onClick={() => { setPontosDesenho([]); setModoDesenho(modoDesenho === 'point' ? 'nenhum' : 'point'); }}
            title="Marcar ponto — clique no mapa pra adicionar"
            style={btnDesenho(modoDesenho === 'point')}>📍</button>
        </div>

        {/* Banner instrução enquanto está desenhando */}
        {modoDesenho !== 'nenhum' && (
          <div style={{
            position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 5,
            padding: '8px 16px', background: '#FF6B00', color: '#fff', borderRadius: 5,
            fontSize: 12, fontWeight: 600, boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span>
              {modoDesenho === 'polygon' && `Clique pra adicionar vértice (${pontosDesenho.length}) · duplo-clique pra fechar (mín. 3)`}
              {modoDesenho === 'line' && `Clique pra adicionar ponto (${pontosDesenho.length}) · duplo-clique pra finalizar (mín. 2)`}
              {modoDesenho === 'point' && 'Clique no mapa para marcar o ponto'}
            </span>
            <button onClick={cancelarDesenho}
              style={{ padding: '3px 9px', fontSize: 11, border: 'none', borderRadius: 3,
                background: 'rgba(255,255,255,0.9)', color: '#FF6B00', cursor: 'pointer', fontWeight: 600 }}>
              Cancelar (Esc)
            </button>
          </div>
        )}

        {/* Popup pra foto do diário clicada */}
        {popupFoto && (
          <div style={{
            position: 'absolute', top: 10, right: 10, zIndex: 7,
            width: 320, background: '#fff', borderRadius: 6,
            border: '0.5px solid #FFB984',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            fontSize: 12,
          }}>
            <div style={{
              padding: '10px 12px', background: '#FFF7F0',
              borderBottom: '0.5px solid #FFB984',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <strong style={{ flex: 1, color: '#B04500' }}>📷 Foto de Campo</strong>
              <button onClick={() => setPopupFoto(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#888' }}>×</button>
            </div>
            <div style={{ padding: '10px 12px' }}>
              {popupFoto._props?.data_diario && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Data do diário</div>
                  <div style={{ fontWeight: 600 }}>{popupFoto._props.data_diario}</div>
                </div>
              )}
              {(popupFoto._props?.encarregado || popupFoto._props?.encarregado_email) && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Encarregado</div>
                  <div>{popupFoto._props.encarregado || popupFoto._props.encarregado_email}</div>
                </div>
              )}
              {popupFoto._props?.nome_arquivo && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Arquivo</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#444' }}>
                    {popupFoto._props.nome_arquivo}
                  </div>
                </div>
              )}
              {popupFoto._props?.descricao && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Descrição</div>
                  <div>{popupFoto._props.descricao}</div>
                </div>
              )}
              {popupFoto._props?.foto_url && (
                <button onClick={() => abrirFoto(popupFoto._props.foto_url)}
                  style={{
                    marginTop: 6, width: '100%', padding: '7px 10px', fontSize: 12,
                    background: '#FF6B00', color: '#fff', border: 'none',
                    borderRadius: 4, cursor: 'pointer', fontWeight: 600,
                  }}>
                  Abrir foto
                </button>
              )}
            </div>
          </div>
        )}

        {/* Popup com metadados da feature clicada */}
        {popupFeature && (
          <div style={{
            position: 'absolute', top: 10, right: 10, zIndex: 6,
            width: 320, maxHeight: '70%', overflowY: 'auto',
            background: '#fff', borderRadius: 6, border: '0.5px solid #CDD4EA',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            fontSize: 12,
          }}>
            <div style={{
              padding: '10px 12px', background: '#FAFBFF', borderBottom: '0.5px solid #e0e0e0',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <strong style={{ flex: 1, color: '#020D7F' }}>
                {ICONES_FEATURE[popupFeature.geometry?.type] || '📍'} Detalhes
              </strong>
              <button onClick={() => setPopupFeature(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#888' }}>×</button>
            </div>
            <div style={{ padding: '10px 12px' }}>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Camada</div>
                <div style={{ color: popupFeature._camadaCor || '#222', fontWeight: 600 }}>
                  {popupFeature._camadaNome}
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Tipo geométrico</div>
                <div>{popupFeature.geometry?.type || '—'}</div>
              </div>
              {popupFeature._areaFeature !== undefined && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Área da feature</div>
                  <div style={{ color: '#020D7F', fontWeight: 600 }}>{fmtArea(popupFeature._areaFeature)}</div>
                </div>
              )}
              {popupFeature._compFeature !== undefined && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Comprimento</div>
                  <div style={{ color: '#020D7F', fontWeight: 600 }}>{fmtDistancia(popupFeature._compFeature)}</div>
                </div>
              )}
              {popupFeature._coords && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Coordenadas</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{popupFeature._coords}</div>
                </div>
              )}
              {popupFeature.properties && Object.keys(popupFeature.properties).length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Propriedades</div>
                  <div style={{ background: '#FAFBFF', padding: 8, borderRadius: 4, border: '0.5px solid #f0f0f0' }}>
                    {Object.entries(popupFeature.properties).map(([k, v]) => (
                      <div key={k} style={{ marginBottom: 3, wordBreak: 'break-word' }}>
                        <span style={{ color: '#666' }}>{k}:</span>{' '}
                        <span style={{ color: '#222' }}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Painel lateral: lista de camadas + métricas + botões */}
      <div style={{
        width: 320, flexShrink: 0, border: '0.5px solid #e0e0e0', borderRadius: 6,
        background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          padding: 12, borderBottom: '0.5px solid #e0e0e0',
          background: '#FAFBFF',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <strong style={{ fontSize: 13, color: '#020D7F' }}>📍 Camadas ({geometrias.length})</strong>
            {/* Botão estilizado via <label> envolvendo input invisível.
                Funciona porque o foco do file dialog não faz mais reload (Onda B fix). */}
            <label style={{
              marginLeft: 'auto', padding: '5px 11px', fontSize: 11, fontWeight: 600,
              border: 'none', borderRadius: 4,
              background: importando ? '#888' : '#0930D0', color: '#fff',
              cursor: importando ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
              userSelect: 'none', whiteSpace: 'nowrap',
            }}>
              {importando ? <><SpinnerInline /> Processando…</> : '+ Adicionar'}
              <input type="file"
                ref={inputFileRef}
                accept=".kml,.kmz,.zip,.geojson,.json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
                disabled={!!importando}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) uploadArquivo(f);
                }}
                style={{ position: 'absolute', width: 1, height: 1, opacity: 0,
                  overflow: 'hidden', clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)' }} />
            </label>
          </div>
          <div style={{ fontSize: 10, color: '#888' }}>
            Aceita: .kml, .kmz, .zip (shapefile) e .geojson
          </div>
        </div>
        {/* Camada de fotos do diário (PWA campo) — toggle de visibilidade */}
        {fotosFC && fotosFC.features && fotosFC.features.length > 0 && (
          <div style={{
            padding: '8px 12px', borderBottom: '0.5px solid #e0e0e0',
            display: 'flex', alignItems: 'center', gap: 8,
            background: fotosVisivel ? '#FFF7F0' : '#fafafa',
          }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: '#FF6B00', flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#020D7F' }}>
              📷 Fotos de Campo ({fotosFC.features.length})
            </span>
            <button onClick={toggleFotosVisivel}
              title={fotosVisivel ? 'Ocultar' : 'Mostrar'}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 14, padding: '2px 4px', color: fotosVisivel ? '#020D7F' : '#999',
              }}>
              {fotosVisivel ? '👁' : '⊘'}
            </button>
          </div>
        )}

        {/* PAINEL DE DIAGNÓSTICO — visível na UI sem precisar de DevTools */}
        {diagnostico.length > 0 && (
          <div style={{
            borderBottom: '0.5px solid #e0e0e0',
            background: diagnostico.some(l => l.nivel === 'erro') ? '#FEE7E7'
                      : diagnostico.some(l => l.nivel === 'ok' && l.texto.startsWith('✓')) ? '#E8F5EE'
                      : '#FFF8E8',
          }}>
            <div style={{
              padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 11, fontWeight: 600,
              cursor: 'pointer',
            }} onClick={() => setDiagAberto(v => !v)}>
              <span>{diagAberto ? '▼' : '▶'}</span>
              <span>
                {diagnostico.some(l => l.nivel === 'erro') ? '❌ Falhou no upload'
                  : diagnostico.some(l => l.texto.startsWith('✓')) ? '✓ Upload concluído'
                  : '⏳ Processando…'}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 10, color: '#666', fontWeight: 400 }}>
                {diagnostico.length} mensagem(ns)
              </span>
            </div>
            {diagAberto && (
              <div style={{
                padding: '4px 12px 10px', maxHeight: 220, overflowY: 'auto',
                fontSize: 10, fontFamily: 'monospace', lineHeight: 1.4,
              }}>
                {diagnostico.map((l, i) => (
                  <div key={i} style={{
                    color: l.nivel === 'erro' ? '#B91626'
                         : l.nivel === 'ok'   ? '#0a7f4a'
                         : '#444',
                    marginBottom: 2,
                    wordBreak: 'break-word',
                  }}>
                    <span style={{ color: '#888', marginRight: 4 }}>[{l.hora}]</span>
                    {l.texto}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button onClick={copiarDiagnostico}
                    style={{ fontSize: 10, padding: '3px 8px',
                      border: '0.5px solid #0930D0', background: '#fff', color: '#0930D0',
                      borderRadius: 3, cursor: 'pointer', fontWeight: 600 }}>
                    📋 Copiar diagnóstico
                  </button>
                  <button onClick={() => { setDiagnostico([]); setDiagAberto(false); }}
                    style={{ fontSize: 10, padding: '3px 8px',
                      border: '0.5px solid #888', background: '#fff', color: '#666',
                      borderRadius: 3, cursor: 'pointer' }}>
                    Limpar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20, color: '#888', textAlign: 'center', fontSize: 12 }}>Carregando…</div>
          ) : geometrias.length === 0 ? (
            <div style={{ padding: 20, color: '#aaa', textAlign: 'center', fontSize: 11, fontStyle: 'italic' }}>
              Nenhuma camada ainda.<br />
              Clique em <strong>+ Adicionar</strong> para subir KMZ/KML/SHP.
            </div>
          ) : (
            geometrias.map(g => (
              <CamadaItem key={g.id}
                g={g}
                onTogglar={(v) => atualizarGeom(g.id, { visivel: v })}
                onCor={(c) => atualizarGeom(g.id, { cor: c })}
                onRenomear={(n) => atualizarGeom(g.id, { nome: n })}
                onCentralizar={() => centralizarEm(g)}
                onExcluir={() => excluirGeom(g)} />
            ))
          )}
        </div>

        {/* Métricas agregadas + export */}
        {geometrias.length > 0 && (
          <div style={{
            padding: 12, borderTop: '0.5px solid #e0e0e0',
            background: '#FAFBFF', fontSize: 11,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <div>
                <div style={{ color: '#888', fontSize: 10 }}>Área total</div>
                <div style={{ color: '#020D7F', fontWeight: 600 }}>{fmtArea(totalArea)}</div>
              </div>
              <div>
                <div style={{ color: '#888', fontSize: 10 }}>Perímetro total</div>
                <div style={{ color: '#020D7F', fontWeight: 600 }}>{fmtDistancia(totalPerim)}</div>
              </div>
              {totalCompr > 0 && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ color: '#888', fontSize: 10 }}>Comprimento (linhas)</div>
                  <div style={{ color: '#020D7F', fontWeight: 600 }}>{fmtDistancia(totalCompr)}</div>
                </div>
              )}
            </div>
            <button onClick={exportarGeoJsonGlobal}
              style={{
                width: '100%', marginTop: 10, padding: '6px 10px', fontSize: 11,
                border: '0.5px solid #0930D0', background: '#fff', color: '#0930D0',
                borderRadius: 4, cursor: 'pointer', fontWeight: 600,
              }}>
              📥 Baixar tudo como GeoJSON
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CamadaItem({ g, onTogglar, onCor, onRenomear, onCentralizar, onExcluir }: {
  g: Geometria;
  onTogglar: (v: boolean) => void;
  onCor: (c: string) => void;
  onRenomear: (n: string) => void;
  onCentralizar: () => void;
  onExcluir: () => void;
}) {
  const [editandoNome, setEditandoNome] = useState(false);
  const [nomeTmp, setNomeTmp] = useState(g.nome);
  const [paletaAberta, setPaletaAberta] = useState(false);

  function salvarNome() {
    if (nomeTmp.trim() && nomeTmp.trim() !== g.nome) onRenomear(nomeTmp.trim());
    setEditandoNome(false);
  }

  return (
    <div style={{
      padding: '10px 12px', borderBottom: '0.5px solid #f0f0f0',
      opacity: g.visivel ? 1 : 0.55,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <input type="checkbox" checked={g.visivel} onChange={e => onTogglar(e.target.checked)}
          style={{ cursor: 'pointer' }} />
        <button onClick={() => setPaletaAberta(v => !v)}
          title="Mudar cor da camada"
          style={{
            width: 14, height: 14, borderRadius: '50%', cursor: 'pointer',
            background: g.cor || '#0930D0', border: '1.5px solid #fff',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.15)', flexShrink: 0,
          }} />
        {editandoNome ? (
          <input value={nomeTmp} autoFocus
            onChange={e => setNomeTmp(e.target.value)}
            onBlur={salvarNome}
            onKeyDown={e => { if (e.key === 'Enter') salvarNome(); if (e.key === 'Escape') { setNomeTmp(g.nome); setEditandoNome(false); } }}
            style={{ flex: 1, fontSize: 12, padding: '2px 4px', border: '0.5px solid #0930D0', borderRadius: 3 }} />
        ) : (
          <span onClick={() => { setNomeTmp(g.nome); setEditandoNome(true); }}
            title="Clique para renomear"
            style={{ flex: 1, fontSize: 12, fontWeight: 500, color: '#222', cursor: 'pointer',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {g.nome}
          </span>
        )}
        <span style={{ fontSize: 9, color: '#888', textTransform: 'uppercase' }}>{g.tipo}</span>
      </div>
      {paletaAberta && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 6, paddingLeft: 24 }}>
          {CORES_PADRAO.map(c => (
            <button key={c} onClick={() => { onCor(c); setPaletaAberta(false); }}
              style={{
                width: 16, height: 16, borderRadius: '50%', cursor: 'pointer',
                background: c, border: c === g.cor ? '2px solid #222' : '1px solid #fff',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
              }} />
          ))}
        </div>
      )}
      <div style={{ paddingLeft: 24, fontSize: 10, color: '#666', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <span>{g.n_features} feature(s)</span>
        {g.area_m2 ? <span>📐 {fmtArea(g.area_m2)}</span> : null}
        {g.perimetro_m ? <span>↻ {fmtDistancia(g.perimetro_m)}</span> : null}
        {g.comprimento_m ? <span>↔ {fmtDistancia(g.comprimento_m)}</span> : null}
      </div>
      <div style={{ paddingLeft: 24, marginTop: 6, display: 'flex', gap: 4 }}>
        <button onClick={onCentralizar} title="Centralizar mapa nesta camada"
          style={{ fontSize: 10, padding: '3px 7px', borderRadius: 3,
            border: '0.5px solid #0930D0', background: '#fff', color: '#0930D0', cursor: 'pointer' }}>
          🎯 Centralizar
        </button>
        <button onClick={onExcluir} title="Excluir camada"
          style={{ fontSize: 10, padding: '3px 7px', borderRadius: 3,
            border: '0.5px solid #B91626', background: '#fff', color: '#B91626',
            cursor: 'pointer', marginLeft: 'auto' }}>
          <IconLixeira />
        </button>
      </div>
    </div>
  );
}

// ─── Estilos base ─────────────────────────────────────────────────────────────
function estiloOsm(): any {
  return {
    version: 8,
    sources: {
      osm: {
        type: 'raster',
        tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'],
        tileSize: 256,
        attribution: '© OpenStreetMap contributors',
      },
    },
    layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
  };
}

function estiloSatelite(): any {
  // Esri World Imagery — gratuito para uso pessoal/comercial leve
  return {
    version: 8,
    sources: {
      satelite: {
        type: 'raster',
        tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
        tileSize: 256,
        attribution: 'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
      },
    },
    layers: [{ id: 'satelite', type: 'raster', source: 'satelite' }],
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function mergeBoundsArray(bboxes: [number, number, number, number][]): [number, number, number, number] {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  for (const b of bboxes) {
    w = Math.min(w, b[0]); s = Math.min(s, b[1]);
    e = Math.max(e, b[2]); n = Math.max(n, b[3]);
  }
  return [w, s, e, n];
}

// Suprimir warning de var não usada
const _unusedBounds: typeof LngLatBounds | undefined = undefined;
void _unusedBounds;
